const FINANCIAL_INSTITUTION_TICKERS = new Set([
  'ITUB4', 'ITUB3', 'BBDC4', 'BBDC3', 'BBAS3', 'BPAC11', 'SANB11', 'SANB3', 'SANB4',
  'BRSR6', 'ABCB4', 'BMGB4', 'BPAN4', 'PINE4', 'BGIP4', 'BMEB4', 'BRBI11',
  'BBSE3', 'CXSE3', 'PSSA3', 'IRBR3', 'SULA11',
  'JPM', 'BAC', 'WFC', 'C', 'GS', 'MS', 'SCHW', 'AXP', 'BLK', 'CB', 'PGR', 'MMC',
  'USB', 'PNC', 'TFC', 'COF', 'MET', 'AIG', 'PRU', 'TRV', 'ALL'
]);

export function isFinancialInstitutionEntity(ticker?: string | null, sector?: string | null, industry?: string | null): boolean {
  const cleanT = (ticker || '').replace(/\.SA$/i, '').trim().toUpperCase();
  if (FINANCIAL_INSTITUTION_TICKERS.has(cleanT)) return true;
  // Exchanges and payment networks have normal EBITDA/margins
  if (['B3SA3', 'V', 'MA', 'CIEL3', 'STNE', 'PAGS', 'PYPL', 'SQ', 'ICE', 'CME', 'NDAQ', 'SPGI', 'MCO'].includes(cleanT)) {
    return false;
  }
  const s = `${sector || ''} ${industry || ''}`.toLowerCase();
  return (
    s.includes('bank') ||
    s.includes('banco') ||
    s.includes('insurance') ||
    s.includes('segur') ||
    s.includes('credit services')
  );
}

export const parseNumber = (val: string | number | undefined | null): number | null => {
  if (val === undefined || val === null || val === "N/D" || val === "-" || val === "-999" || val === -999) return null;
  if (typeof val === 'number') return Number.isFinite(val) ? val : null;
  if (typeof val === 'string') {
    const strVal = val.trim().toUpperCase();
    if (!strVal || strVal === '-' || strVal === 'N/D' || strVal === 'N/A' || strVal === 'NAN') return null;
    const isNegative = strVal.startsWith('-') || (strVal.startsWith('(') && strVal.endsWith(')'));
    
    let multiplier = 1;
    if (strVal.includes('T')) multiplier = 1e12;
    else if (strVal.includes('B')) multiplier = 1e9;
    else if (strVal.includes('M')) multiplier = 1e6;
    else if (strVal.includes('K')) multiplier = 1e3;
    // NOTE: Do NOT multiply '%' by 0.01; all percentages across the app use 0-100 scale.

    const cleaned = strVal.replace(/[^0-9.,]/g, '');
    if (!cleaned) return null;
    
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    
    let parsed: number;
    if (lastComma !== -1 && lastDot !== -1) {
      if (lastComma > lastDot) {
        // Comma is decimal: 1.234,56
        parsed = parseFloat(cleaned.replace(/\./g, '').replace(',', '.'));
      } else {
        // Dot is decimal: 1,234.56
        parsed = parseFloat(cleaned.replace(/,/g, ''));
      }
    } else if (lastComma !== -1) {
      if (cleaned.split(',').length > 2) {
        parsed = parseFloat(cleaned.replace(/,/g, ''));
      } else {
        parsed = parseFloat(cleaned.replace(',', '.'));
      }
    } else if (lastDot !== -1) {
      if (cleaned.split('.').length > 2) {
        parsed = parseFloat(cleaned.replace(/\./g, ''));
      } else {
        const parts = cleaned.split('.');
        if (parts[1] === '000' && parts[0].length <= 3 && multiplier === 1) {
          parsed = parseFloat(cleaned.replace(/\./g, ''));
        } else {
          parsed = parseFloat(cleaned);
        }
      }
    } else {
      parsed = parseFloat(cleaned);
    }
    
    if (isNaN(parsed) || !isFinite(parsed)) return null;
    return (isNegative ? -Math.abs(parsed) : Math.abs(parsed)) * multiplier;
  }
  return null;
};

const processedCompanyCache = new WeakMap<object, any>();

export const getIndicatorValue = (company: any, indicatorName: string, flatKey?: string, indicatorsMap?: Map<string, any>): number | null => {
  if (company.data && flatKey && company.data[flatKey] !== undefined && company.data[flatKey] !== null) {
    const v = parseNumber(company.data[flatKey]);
    if (v !== null) return v;
  }
  if (indicatorsMap) {
    const rawVal = indicatorsMap.get(indicatorName);
    return rawVal !== undefined ? parseNumber(rawVal) : null;
  }
  if (!company.data?.indicators) return null;
  const indicator = company.data.indicators.find((i: any) => i.name === indicatorName);
  if (!indicator) return null;
  return parseNumber(indicator.companyValue);
};

export const processCompanyData = (company: any) => {
  if (!company || typeof company !== 'object') return null;
  if (processedCompanyCache.has(company)) {
    return processedCompanyCache.get(company);
  }
  const data = company.data;
  if (!data) return null;

  const isBankOrInsurer = isFinancialInstitutionEntity(
    company.ticker || data.ticker,
    data.sector,
    data.industry
  );

  const indicatorsMap = new Map<string, any>();
  if (Array.isArray(data.indicators)) {
    for (const ind of data.indicators) {
      if (ind && ind.name) {
        indicatorsMap.set(ind.name, ind.companyValue);
      }
    }
  }

  const getInd = (name: string, flatKey?: string) => getIndicatorValue(company, name, flatKey, indicatorsMap);

  const currentPrice = parseNumber(data.currentPrice) ?? parseNumber(data.price) ?? null;
  const rawMarketCap = parseNumber(data.marketCap);
  const rawShares = parseNumber(data.dcfInputs?.sharesOutstanding) ?? parseNumber(data.sharesOutstanding) ?? null;
  const impliedShares = (rawMarketCap !== null && currentPrice !== null && currentPrice > 0) ? (rawMarketCap / currentPrice) : null;
  const sharesOutstanding = (impliedShares !== null && (rawShares === null || Math.abs(rawShares - impliedShares) / impliedShares > 0.12))
    ? impliedShares
    : (rawShares ?? impliedShares);
  const marketCap = rawMarketCap ?? (currentPrice !== null && sharesOutstanding !== null ? currentPrice * sharesOutstanding : null);

  let peRatio = getInd("P/L", "peRatio") ?? parseNumber(data.advancedMetrics?.additionalMetrics?.peRatio) ?? parseNumber(data?.peRatio) ?? null;
  let psr = getInd("P/SR", "psr") ?? parseNumber(data?.psr) ?? parseNumber(data?.pSr) ?? null;
  let pvp = getInd("P/VP", "pbRatio") ?? parseNumber(data.advancedMetrics?.additionalMetrics?.pbRatio) ?? parseNumber(data?.pbRatio) ?? parseNumber(data?.pvp) ?? null;
  let evEbitda = isBankOrInsurer ? null : (getInd("EV/EBITDA", "evEbitda") ?? parseNumber(data.advancedMetrics?.additionalMetrics?.evEbitda) ?? parseNumber(data?.evEbitda) ?? null);
  let pEbit = isBankOrInsurer ? null : (getInd("P/EBIT", "pEbit") ?? parseNumber(data?.pEbit) ?? null);
  let pAtivo = getInd("P/Ativo", "pAssets") ?? parseNumber(data?.pAssets) ?? parseNumber(data?.pAtivo) ?? null;

  let netIncome = parseNumber(data.netIncome);
  let revenue = parseNumber(data.revenue) ?? (psr !== null && psr !== 0 && marketCap !== null ? marketCap / psr : null);
  const totalEquity = parseNumber(data.totalEquity) ?? (pvp !== null && pvp !== 0 && marketCap !== null ? marketCap / pvp : null);
  const totalAssets = parseNumber(data.totalAssets) ?? (pAtivo !== null && pAtivo !== 0 && marketCap !== null ? marketCap / pAtivo : null);

  let currentLpa = getInd("LPA", "lpa") ?? parseNumber(data?.eps) ?? null;
  let vpa = getInd("VPA", "bvps") ?? parseNumber(data?.bvps) ?? null;

  // Cross-check LPA and VPA against total company shares (Status Invest / Investidor10 standard)
  if (netIncome !== null && sharesOutstanding !== null && sharesOutstanding > 0) {
    const consolidatedLpa = netIncome / sharesOutstanding;
    if (currentLpa === null || (consolidatedLpa !== 0 && Math.abs(currentLpa - consolidatedLpa) / Math.abs(consolidatedLpa) > 0.18)) {
      currentLpa = consolidatedLpa;
    }
  }
  if (totalEquity !== null && sharesOutstanding !== null && sharesOutstanding > 0) {
    const consolidatedVpa = totalEquity / sharesOutstanding;
    if (vpa === null || (consolidatedVpa !== 0 && Math.abs(vpa - consolidatedVpa) / Math.abs(consolidatedVpa) > 0.18)) {
      vpa = consolidatedVpa;
    }
  }

  if (peRatio === null && currentPrice !== null && currentLpa !== null && currentLpa !== 0) {
    peRatio = currentPrice / currentLpa;
  } else if (currentPrice !== null && currentLpa !== null && currentLpa !== 0 && peRatio !== null) {
    const impliedPe = currentPrice / currentLpa;
    if (Math.abs(peRatio - impliedPe) / Math.max(Math.abs(impliedPe), 1) > 0.2) {
      peRatio = impliedPe;
    }
  }

  if (pvp === null && currentPrice !== null && vpa !== null && vpa !== 0) {
    pvp = currentPrice / vpa;
  } else if (currentPrice !== null && vpa !== null && vpa !== 0 && pvp !== null) {
    const impliedPvp = currentPrice / vpa;
    if (Math.abs(pvp - impliedPvp) / Math.max(Math.abs(impliedPvp), 0.1) > 0.2) {
      pvp = impliedPvp;
    }
  }

  if (netIncome === null && peRatio !== null && peRatio !== 0 && marketCap !== null) {
    netIncome = marketCap / peRatio;
  }
  if (psr === null && marketCap !== null && revenue !== null && revenue !== 0) {
    psr = marketCap / revenue;
  }

  const netDebt = isBankOrInsurer ? null : (parseNumber(data.dcfInputs?.netDebt) ?? parseNumber(data.advancedMetrics?.additionalMetrics?.netDebt) ?? parseNumber(data.netDebt) ?? null);
  const cash = parseNumber(data.totalCash) ?? parseNumber(data.cash) ?? (netDebt !== null && netDebt < 0 ? -netDebt : null);
  const ebitda = isBankOrInsurer ? null : (parseNumber(data.ebitda) ?? (evEbitda !== null && evEbitda !== 0 && marketCap !== null ? (marketCap + (netDebt || 0)) / evEbitda : null));
  const ebit = isBankOrInsurer ? null : (parseNumber(data.ebit) ?? (pEbit !== null && pEbit !== 0 && marketCap !== null ? marketCap / pEbit : null));

  if (!isBankOrInsurer && evEbitda === null && marketCap !== null && ebitda !== null && ebitda !== 0) {
    evEbitda = (marketCap + (netDebt || 0)) / ebitda;
  }
  if (!isBankOrInsurer && pEbit === null && marketCap !== null && ebit !== null && ebit !== 0) {
    pEbit = marketCap / ebit;
  }
  if (pAtivo === null && marketCap !== null && totalAssets !== null && totalAssets !== 0) {
    pAtivo = marketCap / totalAssets;
  }

  let roe = (netIncome !== null && totalEquity !== null && totalEquity > 0)
    ? (netIncome / totalEquity) * 100
    : (getInd("ROE", "roe") ?? parseNumber(data.advancedMetrics?.dupontAnalysis?.roe) ?? parseNumber(data?.roe) ?? null);
  let roa = (netIncome !== null && totalAssets !== null && totalAssets > 0)
    ? (netIncome / totalAssets) * 100
    : (getInd("ROA", "roa") ?? parseNumber(data?.roa) ?? null);
  let roic = isBankOrInsurer ? null : (getInd("ROIC", "roic") ?? parseNumber(data?.roic) ?? null);
  let netMargin = (netIncome !== null && revenue !== null && revenue > 0)
    ? (netIncome / revenue) * 100
    : (getInd("Margem Líquida", "netMargin") ?? parseNumber(data.advancedMetrics?.dupontAnalysis?.netMargin) ?? parseNumber(data?.netMargin) ?? null);
  let grossMargin = getInd("Margem Bruta", "grossMargin") ?? parseNumber(data?.grossMargin) ?? null;
  let ebitdaMargin = isBankOrInsurer ? null : ((ebitda !== null && revenue !== null && revenue > 0)
    ? (ebitda / revenue) * 100
    : (getInd("Margem EBITDA", "ebitdaMargin") ?? parseNumber(data?.ebitdaMargin) ?? null));

  if (!isBankOrInsurer && roic === null && ebit !== null && totalEquity !== null && (totalEquity + Math.max(netDebt || 0, 0)) > 0) {
    roic = ((ebit * 0.72) / (totalEquity + Math.max(netDebt || 0, 0))) * 100;
  }

  let debtEbitda = isBankOrInsurer ? null : (getInd("Dívida Líquida/EBITDA", "debtEbitda") ?? getInd("Dívida Líq/EBITDA", "netDebtEbitda") ?? parseNumber(data.advancedMetrics?.additionalMetrics?.debtToEbitda) ?? parseNumber(data?.netDebtEbitda) ?? null);
  if (!isBankOrInsurer && debtEbitda === null && netDebt !== null && ebitda !== null && ebitda !== 0) {
    debtEbitda = netDebt / ebitda;
  }

  let debtEquity = isBankOrInsurer ? null : (getInd("Dívida Líquida/PL", "netDebtEquity") ?? getInd("Dívida Líq/PL", "netDebtEquity") ?? parseNumber(data?.netDebtEquity) ?? null);
  if (!isBankOrInsurer && debtEquity === null && netDebt !== null && totalEquity !== null && totalEquity !== 0) {
    debtEquity = netDebt / totalEquity;
  }

  let neverHadLoss = true;
  if (data.yahooHasLoss) {
    neverHadLoss = false;
  }
  if (currentLpa !== null && currentLpa < 0) neverHadLoss = false;
  if (peRatio !== null && peRatio < 0) neverHadLoss = false;
  if (netIncome !== null && netIncome < 0) neverHadLoss = false;

  if (neverHadLoss && data.historicalIndicators && data.historicalIndicators.length > 0) {
    for (const yearData of data.historicalIndicators) {
      const lpa = parseNumber(yearData.lpa);
      const lucroLiquido = parseNumber(yearData.lucroLiquido);
      const netIncomeHist = parseNumber(yearData.netIncome);
      
      if ((lpa !== null && lpa < 0) || (lucroLiquido !== null && lucroLiquido < 0) || (netIncomeHist !== null && netIncomeHist < 0)) {
        neverHadLoss = false;
        break;
      }
    }
  }

  const grahamFairValue = (currentLpa !== null && currentLpa > 0 && vpa !== null && vpa > 0)
    ? Math.sqrt(22.5 * currentLpa * vpa)
    : (parseNumber(data.advancedMetrics?.grahamFairValue) ?? null);
  const peterLynchFairValue = parseNumber(data.advancedMetrics?.peterLynchFairValue) ?? null;
  const bazinFairValue = parseNumber(data.advancedMetrics?.bazinFairValue) ?? null;

  const result = {
    ticker: company.ticker,
    companyName: data.company_name || data.companyName || company.company_name,
    currentPrice,
    marketCap,
    netIncome,
    revenue,
    cash,
    netDebt,
    peRatio,
    roe,
    roa,
    roic,
    netMargin,
    grossMargin,
    ebitdaMargin,
    dividendYield: getInd("Dividend Yield", "dividendYield") ?? parseNumber(data.advancedMetrics?.additionalMetrics?.dividendYield) ?? parseNumber(data?.dividendYield) ?? null,
    revenueGrowth5y: getInd("CAGR Receita 5a", "revenueGrowth5yr") ?? parseNumber(data?.revenueGrowth5y) ?? parseNumber(data?.revenueGrowth5yr) ?? null,
    profitGrowth5y: getInd("CAGR Lucro 5a", "epsGrowth5yr") ?? parseNumber(data?.profitGrowth5y) ?? parseNumber(data?.epsGrowth5yr) ?? null,
    pvp,
    evEbitda,
    pEbit,
    psr,
    pAtivo,
    debtEbitda,
    debtEquity,
    currentRatio: isBankOrInsurer ? null : (getInd("Liquidez Corrente", "currentRatio") ?? parseNumber(data?.currentRatio) ?? null),
    lpa: currentLpa,
    vpa,
    neverHadLoss,
    upside: parseNumber(data.upside) ?? null,
    piotroskiScore: parseNumber(data.advancedMetrics?.piotroskiScore) ?? parseNumber(data.piotroskiFScore) ?? null,
    altmanZScore: isBankOrInsurer ? null : (parseNumber(data.advancedMetrics?.altmanZScore) ?? parseNumber(data.altmanZScore) ?? null),
    beneishMScore: isBankOrInsurer ? null : (parseNumber(data.advancedMetrics?.beneishMScore) ?? parseNumber(data.beneishMScore) ?? null),
    pegRatio: getInd("PEG Ratio", "pegRatio") ?? parseNumber(data?.pegRatio) ?? null,
    payoutRatio: getInd("Payout Ratio", "payoutRatio") ?? parseNumber(data?.payoutRatio) ?? null,
    quickRatio: isBankOrInsurer ? null : (getInd("Liquidez Seca", "quickRatio") ?? parseNumber(data?.quickRatio) ?? null),
    fcfYield: getInd("FCF Yield", "fcfYield") ?? parseNumber(data?.fcfYield) ?? null,
    reverseDcfImpliedGrowth: parseNumber(data.advancedMetrics?.reverseDcfImpliedGrowth) ?? null,
    assetTurnover: parseNumber(data.advancedMetrics?.dupontAnalysis?.assetTurnover) ?? parseNumber(data.assetTurnover) ?? (revenue !== null && totalAssets !== null && totalAssets !== 0 ? revenue / totalAssets : null),
    equityMultiplier: parseNumber(data.advancedMetrics?.dupontAnalysis?.equityMultiplier) ?? (totalAssets !== null && totalEquity !== null && totalEquity !== 0 ? totalAssets / totalEquity : null),
    wacc: parseNumber(data.dcfInputs?.wacc) ?? null,
    terminalGrowthRate: parseNumber(data.dcfInputs?.terminalGrowthRate) ?? null,
    costOfEquity: parseNumber(data.dcfInputs?.costOfEquity) ?? null,
    costOfDebt: parseNumber(data.dcfInputs?.costOfDebt) ?? null,
    projectionGrowthRate: parseNumber(data.dcfInputs?.projectionGrowthRate) ?? null,
    fcf: parseNumber(data.dcfInputs?.fcf) ?? parseNumber(data.fcf) ?? null,
    sharesOutstanding,
    fairValue: parseNumber(data.fairValue) ?? null,
    grahamFairValue,
    peterLynchFairValue,
    bazinFairValue,
    grahamUpside: grahamFairValue !== null && currentPrice !== null && currentPrice > 0 ? (grahamFairValue / currentPrice - 1) * 100 : null,
    bazinUpside: bazinFairValue !== null && currentPrice !== null && currentPrice > 0 ? (bazinFairValue / currentPrice - 1) * 100 : null,
    peterLynchUpside: peterLynchFairValue !== null && currentPrice !== null && currentPrice > 0 ? (peterLynchFairValue / currentPrice - 1) * 100 : null,
  };

  processedCompanyCache.set(company, result);
  return result;
};


